import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Check, ShieldCheck, ArrowRight, Lock, Tag } from "lucide-react";
import { toast } from "sonner";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { SERVICES, getService, DELIVERY_OPTIONS } from "@/data/services";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

export default function Order() {
  const [params] = useSearchParams();
  const [selected, setSelected] = useState(params.get("package") || "cv-writing");
  const [speed, setSpeed] = useState("basic");
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null); // {code, percent, discount, label}
  const [promoError, setPromoError] = useState("");
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const p = params.get("package");
    if (p && getService(p)) setSelected(p);
  }, [params]);

  const service = getService(selected);
  const delivery = DELIVERY_OPTIONS[speed];
  const baseTotal = service.price + delivery.fee;
  const total = Math.max(baseTotal - (promo?.discount || 0), 1);

  const applyPromo = async () => {
    setPromoError("");
    if (!promoInput.trim()) return;
    try {
      const { data } = await api.post("/promo/validate", { code: promoInput.trim(), amount: baseTotal });
      setPromo(data);
      toast.success(`${data.label} applied — £${data.discount.toFixed(2)} off`);
    } catch (err) {
      setPromo(null);
      setPromoError(formatApiError(err));
    }
  };

  const checkout = async () => {
    if (!user) {
      navigate(`/auth?next=${encodeURIComponent(`/order?package=${selected}`)}`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: selected,
        origin_url: window.location.origin,
        delivery_speed: speed,
        promo_code: promo?.code || null,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  return (
    <div data-testid="order-page">
      <section className="mx-auto max-w-7xl px-5 pb-24 pt-20 lg:px-8 lg:pt-28">
        <span className="overline-label">Order / Intake</span>
        <h1 className="mt-5 max-w-3xl font-serif text-4xl font-semibold leading-tight text-slate-50 sm:text-5xl" data-testid="order-headline">
          <MaskedLine delay={0.1}>Select your package.</MaskedLine>
          <MaskedLine delay={0.3} className="italic text-[#C5A059]">Checkout in GBP.</MaskedLine>
        </h1>

        <div className="mt-14 grid gap-10 lg:grid-cols-5">
          {/* PACKAGE SELECT */}
          <div className="space-y-4 lg:col-span-3">
            {SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.04}>
                <button
                  onClick={() => setSelected(s.id)}
                  data-testid={`order-package-${s.id}`}
                  className={`w-full rounded-2xl border p-6 text-left transition-colors duration-200 ${
                    selected === s.id ? "gold-stroke bg-[#111827]" : "border-slate-700/50 bg-[#0F172A]/60 hover:border-slate-600"
                  }`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <span className={`flex h-5 w-5 items-center justify-center rounded-full border ${selected === s.id ? "border-[#C5A059] bg-[#C5A059]" : "border-slate-600"}`}>
                        {selected === s.id && <Check size={12} className="text-[#0A0F1D]" />}
                      </span>
                      <div>
                        <div className="flex items-center gap-3">
                          <span className="font-serif text-xl font-semibold text-slate-50">{s.name}</span>
                          {s.popular && <span className="rounded-full bg-[#C5A059] px-2.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-[0.15em] text-[#0A0F1D]">Best Value</span>}
                        </div>
                        <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">{s.turnaround}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-2xl text-slate-50">£{s.price.toFixed(2)}</div>
                      {s.originalValue && <div className="font-mono text-xs text-slate-500 line-through">£{s.originalValue.toFixed(2)}</div>}
                    </div>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>

          {/* DELIVERY SPEED */}
          <div className="lg:col-span-3">
            <Reveal delay={0.25}>
              <h2 className="font-serif text-2xl font-semibold text-slate-50" data-testid="delivery-speed-heading">Choose your delivery speed</h2>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                {Object.values(DELIVERY_OPTIONS).map((d) => (
                  <button
                    key={d.id}
                    onClick={() => setSpeed(d.id)}
                    data-testid={`delivery-option-${d.id}`}
                    className={`rounded-2xl border p-6 text-left transition-colors duration-200 ${
                      speed === d.id ? "gold-stroke bg-[#111827]" : "border-slate-700/50 bg-[#0F172A]/60 hover:border-slate-600"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`flex h-5 w-5 items-center justify-center rounded-full border ${speed === d.id ? "border-[#C5A059] bg-[#C5A059]" : "border-slate-600"}`}>
                        {speed === d.id && <Check size={12} className="text-[#0A0F1D]" />}
                      </span>
                      <span className="font-mono text-sm text-slate-300">{d.fee === 0 ? "Included" : `+£${d.fee.toFixed(2)}`}</span>
                    </div>
                    <div className="mt-4 font-semibold text-slate-50">{d.label}</div>
                    <div className="mt-1 font-mono text-xs uppercase tracking-[0.12em] text-[#C5A059]">{d.days}</div>
                  </button>
                ))}
              </div>
            </Reveal>
          </div>

          {/* SUMMARY */}
          <Reveal delay={0.2} className="lg:col-span-2">
            <div className="card-surface sticky top-28 p-8" data-testid="order-summary">
              <h2 className="font-serif text-2xl font-semibold text-slate-50">Order Summary</h2>
              <div className="mt-6 space-y-4 border-b border-slate-700/60 pb-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-semibold text-slate-100">{service.name}</div>
                    <div className="mt-1 text-xs text-slate-500">{service.turnaround}</div>
                  </div>
                  <span className="font-mono text-lg text-slate-50">£{service.price.toFixed(2)}</span>
                </div>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-semibold text-slate-100">{delivery.label}</div>
                    <div className="mt-1 text-xs text-slate-500">{delivery.days}</div>
                  </div>
                  <span className="font-mono text-lg text-slate-50">{delivery.fee === 0 ? "£0.00" : `£${delivery.fee.toFixed(2)}`}</span>
                </div>
              </div>
              <ul className="mt-6 space-y-2.5">
                {service.features.slice(0, 4).map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-xs leading-relaxed text-slate-400">
                    <Check size={13} className="mt-0.5 shrink-0 text-[#C5A059]" /> {f}
                  </li>
                ))}
              </ul>

              {/* PROMO CODE */}
              <div className="mt-6 border-t border-slate-700/60 pt-6">
                <label className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-slate-400" htmlFor="promo-code-input">
                  <Tag size={13} className="text-[#C5A059]" /> Referral code
                </label>
                <div className="flex gap-2">
                  <input
                    id="promo-code-input"
                    data-testid="promo-code-input"
                    value={promoInput}
                    onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromo(null); setPromoError(""); }}
                    className="input-dark flex-1 !py-2.5 font-mono uppercase"
                    placeholder="e.g. FRIEND10"
                  />
                  <button type="button" onClick={applyPromo} className="btn-ghost !px-5 !py-2.5" data-testid="promo-apply-btn">Apply</button>
                </div>
                {promo && (
                  <p className="mt-2 text-xs font-semibold text-emerald-400" data-testid="promo-success">
                    {promo.label}: −£{promo.discount.toFixed(2)} ({promo.percent}% off)
                  </p>
                )}
                {promoError && <p className="mt-2 text-xs text-red-400" data-testid="promo-error">{promoError}</p>}
                {!promo && !promoError && (
                  <p className="mt-2 text-xs text-slate-500">Know a student who's used us? Ask for their referral code — try FRIEND10 for 10% off.</p>
                )}
              </div>

              {promo && (
                <div className="mt-4 flex items-center justify-between text-sm">
                  <span className="text-slate-400">Discount ({promo.code})</span>
                  <span className="font-mono text-emerald-400">−£{promo.discount.toFixed(2)}</span>
                </div>
              )}
              <div className="mt-6 flex items-center justify-between border-t border-slate-700/60 pt-6">
                <span className="text-sm font-semibold text-slate-300">Total (GBP)</span>
                <span className="font-mono text-3xl font-semibold text-[#C5A059]" data-testid="order-total">£{total.toFixed(2)}</span>
              </div>

              <button onClick={checkout} disabled={busy} className="btn-gold mt-8 w-full disabled:opacity-60" data-testid="checkout-stripe-submit-btn">
                {busy ? "Redirecting to Stripe…" : user ? `Pay £${total.toFixed(2)} Securely` : "Sign In & Pay"} <ArrowRight size={16} />
              </button>
              {!user && (
                <p className="mt-3 text-center text-xs text-slate-500" data-testid="order-login-hint">
                  You'll sign in or create an account first — it takes 20 seconds.
                </p>
              )}
              <div className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-500">
                <Lock size={13} className="text-emerald-400" />
                <span>256-bit SSL · Stripe · Debit / Credit / Apple Pay</span>
              </div>
              <div className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-500">
                <ShieldCheck size={13} className="text-[#C5A059]" />
                <span>Next step after payment: career questionnaire + CV upload</span>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.3}>
          <p className="mt-12 text-center text-sm text-slate-500">
            Need a custom corporate quote instead? <Link to="/contact" className="text-[#C5A059] hover:underline" data-testid="order-contact-link">Contact us</Link>
          </p>
        </Reveal>
      </section>
    </div>
  );
}
